// src/components/Auth/LoginExpiredDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import { getLangFromCookie, LANG_COOKIE } from "../../../Utils/LangCookie";

/**
 * Helper: trigger the dialog from anywhere
 * Usage:
 *   window.dispatchEvent(new CustomEvent("app:authExpired", { detail: { reason: "401" } }))
 */
export const AUTH_EXPIRED_EVENT = "app:authExpired";

/* ------------------------- i18n helpers ------------------------- */

const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

function getCurrentLangCode() {
  if (typeof window === "undefined") return "ZH";
  const fromCookie = getLangFromCookie?.();
  const fromLS = window.localStorage.getItem(LANG_COOKIE);
  return normalizeLang(fromCookie || fromLS || "ZH");
}

const TEXT = {
  ZH: {
    title: "登入已過期",
    msg: "您的登入狀態已過期，因此系統已自動登出。請重新登入。",
    cta: "前往登入",
  },
  EN: {
    title: "Session expired",
    msg: "Your login session has expired, so you have been logged out automatically. Please log in again.",
    cta: "Go to login",
  },
  JP: {
    title: "セッションの有効期限が切れました",
    msg: "ログインの有効期限が切れたため、自動的にログアウトされました。再度ログインしてください。",
    cta: "ログインへ",
  },
};

function tForLang(lang, key) {
  const l = normalizeLang(lang);
  return TEXT[l]?.[key] ?? TEXT.ZH?.[key] ?? key;
}

/**
 * LoginExpiredDialog
 *
 * Props:
 * - open: boolean
 * - mode: "b2b" | "b2c"
 * - onConfirm: () => void
 * - onClose: () => void
 */
export default function LoginExpiredDialog({ open, mode, onConfirm, onClose }) {
  const [lang, setLang] = useState(getCurrentLangCode());

  // ✅ listen to language changes (cookie/localStorage changes + your custom event)
  useEffect(() => {
    const handler = () => setLang(getCurrentLangCode());
    window.addEventListener("app:languageChanged", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("app:languageChanged", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const title = useMemo(() => tForLang(lang, "title"), [lang]);
  const msg = useMemo(() => tForLang(lang, "msg"), [lang]);
  const cta = useMemo(() => tForLang(lang, "cta"), [lang]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      aria-labelledby="login-expired-title"
      aria-describedby="login-expired-desc"
    >
      <DialogTitle id="login-expired-title">{title}</DialogTitle>

      <DialogContent>
        <DialogContentText id="login-expired-desc">{msg}</DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onConfirm} variant="contained">
          {cta}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
